/**
 * RedwoodScrapyard — Level 1.
 *
 * Giant redwoods strangled by rusted motorcycle parts.
 * Theme: Overcoming Inertia.
 *
 * Puzzles (in order) — each is a self-contained `PuzzleModule` under
 * `../levels/puzzles/redwood-scrapyard/`:
 *   1. The Seized Crane      — `SeizedCranePuzzle`
 *   2. The Crow vs. Squirrel — `CrowSquirrelPuzzle`
 *   3. The Kitten Corral     — `KittenCorralPuzzle`
 *   4. The Hoodie Papoose    — `HoodieJigsawPuzzle` (apex)
 *   5. Final Blockage        — `FinalBlockagePuzzle` (gates on 1-4)
 *
 * This file is now the scene shell only: boot, camera, lighting, post-
 * processing, environment art, the Elder Leo encounter, Resonant Drop
 * unlock, and level completion. All puzzle geometry + logic lives in
 * the puzzle modules.
 *
 * Elder Leo's cutscene is triggered by `BLOCKAGE_SHATTERED` — the co-op
 * ability reward is taught during that cutscene and the exit portal
 * opens at the end.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { SpotLight } from "@babylonjs/core/Lights/spotLight";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import { applyVisualProfile, REDWOOD_SCRAPYARD_PROFILE } from "../levels";
import {
  ParallaxBackground,
  paintGradient,
  paintTreeSilhouette,
  paintHazeBand,
} from "../systems/ParallaxBackground";
import { getOrMakeNoiseBump } from "../systems/ProceduralNoise";

import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";

import { getAssetManager, InstantiatedModel } from "../systems/AssetManager";
import {
  freezeStaticMeshes,
  DEFAULT_STATIC_EXCLUDES,
  scatterInstances,
  type ScatterPlacement,
} from "../levels/staticOptimization";
import { tagAs } from "../levels";
import { PhysicsSystem } from "../systems/PhysicsSystem";
import { CameraSystem } from "../systems/CameraSystem";
import { InputManager } from "../systems/InputManager";
import { AbilityHolder } from "../systems/AbilitySystem";
import { AudioManager } from "../systems/AudioManager";
import { CutsceneSystem, CutsceneStep } from "../systems/CutsceneSystem";
import { ELDER_KITTY_ENCOUNTER, LEVEL_INTRO, controlHintForMode } from "../config/dialogue";

import { Portal } from "../entities/interactives/Portal";
import { GreaseCache } from "../entities/interactives/GreaseCache";
import { PapooseCarrier } from "../entities/interactives/PapooseCarrier";
import { ElderKitty } from "../entities/npcs/ElderKitty";

import { InteractiveInfo } from "../abilities/minna/AuraSight";
import { ShadowZoneInfo } from "../abilities/minna/ShadowSlip";
import { ResonantDrop } from "../abilities/coop/ResonantDrop";
import { LevelCueDriver } from "../levels/LevelCueDriver";

import { SCRAPYARD, COLORS, WORLD_DEBUG } from "../config/GameConfig";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";

import { setupBaseLevel } from "./BaseLevel";
import { tryCoopAbility } from "./coopAbility";
import { setupElderBiscuitAnchor } from "./setupElderBiscuitAnchor";

import { REDWOOD_SCRAPYARD_PLATFORMS, platformDepth, platformHeight } from "../levels/layouts";
import type { PuzzleContext, PuzzleModule, PuzzleState } from "../levels/PuzzleModule";
import { SeizedCranePuzzle } from "../levels/puzzles/redwood-scrapyard/SeizedCrane";
import { CrowSquirrelPuzzle } from "../levels/puzzles/redwood-scrapyard/CrowSquirrel";
import { KittenCorralPuzzle } from "../levels/puzzles/redwood-scrapyard/KittenCorral";
import { HoodieJigsawPuzzle } from "../levels/puzzles/redwood-scrapyard/HoodieJigsaw";
import { FinalBlockagePuzzle } from "../levels/puzzles/redwood-scrapyard/FinalBlockage";

// ─── Types ──────────────────────────────────────────────────────────

export interface RedwoodScrapyardContext {
  physicsSystem: PhysicsSystem;
  cameraSystem: CameraSystem;
  inputManager: InputManager;
  minna: import("../entities/players/Minna").Minna;
  basil: import("../entities/players/Basil").Basil;
  minnaAbilities: AbilityHolder;
  basilAbilities: AbilityHolder;
  audioManager: AudioManager;
  cutsceneSystem: CutsceneSystem;
}

// ─── Main Setup ─────────────────────────────────────────────────────

/**
 * Set up the Redwood Scrapyard scene.
 * Call after the engine and scene are created and physics is initialized.
 */
export async function setupRedwoodScrapyard(scene: Scene): Promise<RedwoodScrapyardContext> {
  // --- Base level setup ---
  const base = await setupBaseLevel(scene, {
    levelKey: "RedwoodScrapyard",
    clearColor: new Color4(0.04, 0.03, 0.09, 1.0),
    fogColor: Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_DARK),
    fogDensity: 0.007,
    // Spawn staggered around p1_1's center (x=7, width=10, spans
    // x∈[2, 12]). Prior spawn at x=5 sat 3u from the west edge — a
    // single reflex `d`-tap (screen-right = world-negative-X) walked
    // the cat off in <1s. New spawn at x=6/8 has ~4u margin both
    // sides. Same pattern as the L3 spawn fix.
    minnaSpawn: new Vector3(6, 4, 0),
    basilSpawn: new Vector3(8, 4, 0),
  });

  const {
    physics: physicsSystem, minna, basil, minnaAbilities, basilAbilities,
    auraSight, shadowSlip, calmingResonance, turnTableTouch, pneumaticAnchor,
    camera: cameraSystem, audioManager, cutsceneSystem, inputManager,
  } = base;

  // --- Visual profile: lights, shadows, post-process, glow, fog, clearColor ---
  const visualHandles = applyVisualProfile(scene, REDWOOD_SCRAPYARD_PROFILE, {
    glowLayer: base.getGlowLayer(),
  });
  base.addDisposeCallback(() => visualHandles.dispose());

  const glowLayer = visualHandles.glowLayer;
  const shadowGen = visualHandles.shadowGenerator!;
  base.shadowGenerator = shadowGen;

  // --- Redwood-specific bioluminescent SpotLights ---
  // The AccentLightPreset type only supports PointLight, so these cool
  // moss-glow SpotLights remain inline. They're meant to be a subtle
  // canopy accent — a few sage-green beams peeking through — between
  // the warm candle points the profile already placed.
  //
  // Prior tuning used 7 points × intensity 0.6 × range 14 of
  // fully-saturated #00ff88 neon green. That covered the entire
  // playable range and saturated the scene toward a flat emerald
  // wash (2026-04-18 visual review). Canon ("gothic steampunk
  // whimsy" → jewel tones + brass) calls for deep moody palette
  // dominated by fog/shadow/warm candles; the green should be an
  // accent, not the base light.
  //
  // Revised: fewer points (4 instead of 7) at half intensity,
  // tighter range, muted sage-green (#3f7a52) rather than neon.
  const BIO_SAGE = new Color3(0.25, 0.48, 0.32);
  const bioPositions = [
    new Vector3(25, 7, -2),
    new Vector3(70, 6, -2),
    new Vector3(90, 6, -2),
    new Vector3(140, 6, -2),
  ];
  for (let i = 0; i < bioPositions.length; i++) {
    const bio = new SpotLight(
      `bioAccent_${i}`,
      bioPositions[i],
      new Vector3(0.1, -1, 0.3),
      Math.PI / 5,
      2,
      scene,
    );
    bio.diffuse = BIO_SAGE;
    bio.intensity = 0.18;
    bio.range = 9;
  }

  // --- Environment ---
  // Phase C3 replaced the inline skyPlane + background-trunk boxes with
  // painted parallax layers below. `createFogLayers` + `createAtmosphereMotes`
  // stay — they sit between parallax and the playfield and add depth
  // without occluding the painted backdrop. The old `createBackground`
  // drew an opaque skyPlane at z=-15 that hid the parallax entirely.
  createFogLayers(scene);
  const atmosphereMotesPS = createAtmosphereMotes(scene);
  cameraSystem.registerAmbientParticles([atmosphereMotesPS]);
  const shadowZones = createShadowZones(scene);

  // --- Parallax redwood canopy (Phase C3) ---
  // The book's Scrapyard concept art is layered: starry dusk sky, a band
  // of distant redwood silhouettes, then the rusted foreground. These
  // three layers give the shot depth without new GLBs.
  const scrapyardParallax = new ParallaxBackground(scene, cameraSystem.camera, [
    {
      name: "scrapyardDuskSky",
      z: -48,
      scrollFactor: 0.9,
      width: 260,
      height: 70,
      yOffset: 12,
      alpha: 0.85,
      paint: paintGradient("#0b0718", "#2a1a2a"),
    },
    {
      name: "scrapyardFarRedwoods",
      z: -26,
      scrollFactor: 0.45,
      width: 220,
      height: 36,
      yOffset: 6,
      alpha: 0.9,
      paint: paintTreeSilhouette("#08050d", 0.45, 0.92, 120),
    },
    {
      // Softened + pushed back to z=-12 (was z=-6, alpha 0.35).
      // Near haze at z=-6 with alpha 0.35 overlapped the playfield
      // and made the scene feel out of focus. New position puts it
      // between mid-parallax (z=-26) and near play plane (z=0) with
      // lighter opacity so it reads as atmospheric depth, not fog
      // smeared over the cats.
      name: "scrapyardNearHaze",
      z: -12,
      scrollFactor: 0.25,
      width: 140,
      height: 20,
      yOffset: 2.8,
      alpha: 0.18,
      paint: paintHazeBand("rgba(90,70,110,0.4)", 0.55),
    },
  ]);
  base.addDisposeCallback(
    base.registerSafeBeforeRender("scrapyardParallax", () => scrapyardParallax.update()),
  );
  base.addDisposeCallback(() => scrapyardParallax.dispose());

  // --- Ground & Platforms ---
  const { ground, platforms } = createGroundAndPlatforms(scene, physicsSystem, shadowGen);

  // --- Load 3D environment model (visual shell over physics colliders) ---
  let envInstance: InstantiatedModel | null = null;
  const envMeshes: AbstractMesh[] = [];
  try {
    envInstance = await getAssetManager().instantiateModel(
      scene,
      "/assets/models/environment/scrapyard_env.glb",
    );

    // Position/scale the env root to align with physics colliders
    const envRoot = envInstance.rootNodes[0];
    if (envRoot instanceof TransformNode) {
      // Shift the entire environment backward by 6 units.
      // This evacuates the camera corridor (z > 0) without tearing meshes apart.
      envRoot.position.set(0, 0, -6.0);
    }

    // Enable shadow receiving on all env meshes, and evacuate the camera
    // corridor: any scenery authored in z ∈ [0.5, 8] is between the cats and
    // the camera at z=+9 and occludes the gameplay plane. Push those meshes
    // behind z=0 so the playing field stays readable. Also split depth: ~30%
    // of the remaining scenery gets pushed further back (z<=-8) for parallax.
    for (const mesh of envInstance.meshes) {
      // Q-039: the GLB's BG_Foliage_* planes are authored HORIZONTAL at
      // y=10-14 (no X rotation), so the side-scroll camera sees them
      // edge-on — they read as flat mint slivers, and their shared
      // `Foliage` material carries a minty emissive glow. The painted
      // Phase-C3 parallax below (dusk gradient + tree silhouettes) is
      // the canonical backdrop, so disable the panels outright. Also hide
      // Ground_Floor which is an untextured flat green polygon. Clone
      // names from instantiateModelsToScene keep the source node name,
      // so the regex matches all ~24 dupes (_s2/_s3/_s4 included).
      // Perf bonus on the fps-tightest level: removes ~24 alpha-blended
      // meshes. MUST stay before freezeStaticMeshes below.
      if (/BG_Foliage/i.test(mesh.name) || /Ground_Floor/i.test(mesh.name)) {
        mesh.setEnabled(false);
        continue;
      }
      mesh.receiveShadows = true;
      envMeshes.push(mesh);
    }
  } catch (err) {
    console.warn("[RedwoodScrapyard] Failed to load environment model, using procedural geometry:", err);
  }

  // Ground/platforms stay visible even with the authored GLB loaded — the
  // scrapyard env doesn't include walkable geometry covering the platform
  // staircase, so hiding them leaves players jumping onto invisible ledges.
  // We keep the physics bodies AND the visuals.

  // Clean up the instantiated clones on scene dispose. The AssetManager's
  // scene-scoped cache is released automatically when the owning scene is
  // disposed.
  if (envInstance) {
    const instance = envInstance;
    // Freeze static world matrices + materials on the scrapyard env so
    // Babylon doesn't recompute transforms every frame for ~1000
    // scenery meshes that never move.
    freezeStaticMeshes(envMeshes, { excludePatterns: DEFAULT_STATIC_EXCLUDES });
    base.addDisposeCallback(() => {
      instance.dispose();
    });
  }

  // Add players as shadow casters
  shadowGen.addShadowCaster(minna.mesh);
  shadowGen.addShadowCaster(basil.mesh);

  // --- Co-op ability (available from level start; Resonant Drop is taught
  //     in the Elder Leo cutscene — the ability object lives throughout) ---
  const resonantDrop = new ResonantDrop(scene, basil.mesh, minna.mesh);
  basilAbilities.addAbility(resonantDrop);

  // --- Shadow zones ---
  shadowSlip.registerShadowZones(shadowZones);

  // ─── Puzzle Modules ──────────────────────────────────────────────
  //
  // Section layout (world X positions):
  //   Section 1:   0-30  (Puzzle 1 - Seized Crane)
  //   Section 2:  30-70  (Puzzle 2 - Crow vs Squirrel)
  //   Section 3:  70-110 (Puzzle 3 - Kitten Corral)
  //   Section 4: 110-135 (Puzzle 4 - Apex Jigsaw)
  //   Section 5: 135-154 (Puzzle 5 - Final Blockage + Elder)

  const seizedCrane = new SeizedCranePuzzle();
  const crowSquirrel = new CrowSquirrelPuzzle();
  const kittenCorral = new KittenCorralPuzzle();
  const hoodieJigsaw = new HoodieJigsawPuzzle();
  const finalBlockage = new FinalBlockagePuzzle();

  const puzzles: PuzzleModule[] = [
    seizedCrane,
    crowSquirrel,
    kittenCorral,
    hoodieJigsaw,
    finalBlockage,
  ];

  // Level-scoped shared puzzle state (read by finalBlockage to gate on
  // the other four). Each puzzle writes into this map each frame via the
  // game-loop wrapper below.
  const levelState = new Map<string, PuzzleState>();

  // AuraSight interactive collection — puzzles contribute their meshes
  // here via `ctx.registerInteractive`, then we pass the full list to the
  // ability once setup is complete and add the scene-level connections.
  const allInteractives: InteractiveInfo[] = [];

  // Hoodie Papoose — created up front so both the apex jigsaw and the
  // kitten corral can share a single carrier via the puzzle context.
  // Activation is deferred until the Jigsaw solves (canon book p.13):
  // the carrier starts inactive and `activate()` fires on solve.
  const papooseCarrier = new PapooseCarrier(scene, minna.mesh);
  base.addDisposeCallback(() => papooseCarrier.dispose());

  const puzzleCtx: PuzzleContext = {
    scene,
    physicsSystem,
    glowLayer,
    shadowGenerator: shadowGen,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    auraSight,
    shadowSlip,
    calmingResonance,
    turnTableTouch,
    pneumaticAnchor,
    audioManager,
    cutsceneSystem,
    gameEvents,
    levelState,
    registerInteractive: (info) => {
      allInteractives.push(info);
    },
    addUnsub: (unsub) => base.addUnsub(unsub),
    papooseCarrier,
  };

  // Setup all puzzles.
  for (const puzzle of puzzles) {
    await puzzle.setup(puzzleCtx);
    levelState.set(puzzle.id, puzzle.getState());
  }

  // Affordance pass — boost emissive on every registered interactive so it
  // reads as "you can act on this" through fog at 10-15m. Walks each root
  // and its descendants; tints PBRMaterial emissive toward warm brass.
  // Skipped if AuraSight already owns the mesh's highlight.
  //
  // Was bioluminescent cyan (0.08, 0.45, 0.55) — a flat teal wash over
  // every interactive that read as a debug highlight and pulled the whole
  // frame away from the brass/jewel canon palette (2026-07-10 visual
  // review, finding #10). Warm brass keeps the same "you can act on
  // this" luminance step while staying inside the level's palette.
  const affordanceRim = new Color3(0.52, 0.34, 0.12);
  const seenMats = new Set<string>();
  for (const info of allInteractives) {
    const root = info.mesh;
    const stack: AbstractMesh[] = [root];
    while (stack.length > 0) {
      const m = stack.pop();
      if (!m) continue;
      for (const c of m.getChildMeshes(false)) stack.push(c);
      const mat = m.material;
      if (!mat || seenMats.has(mat.name)) continue;
      seenMats.add(mat.name);
      if (
        (mat as typeof mat & { metadata?: { preserveAffordanceEmissive?: boolean } })
          .metadata?.preserveAffordanceEmissive
      ) {
        continue;
      }
      if (mat instanceof PBRMaterial) {
        mat.emissiveColor = mat.emissiveColor.add(affordanceRim.scale(0.6));
        mat.emissiveIntensity = Math.max(mat.emissiveIntensity ?? 1, 1.4);
      } else if ("emissiveColor" in mat) {
        (mat as unknown as { emissiveColor: Color3 }).emissiveColor =
          affordanceRim.scale(0.7);
      }
    }
  }

  // Expose loaded puzzle IDs for runtime parity testing against the
  // level's MechanicContract (see tests/integration/puzzle-id-parity.spec.ts).
  scene.metadata = {
    ...(scene.metadata ?? {}),
    loadedPuzzleIds: puzzles.map((p) => p.id),
  };

  // Composite puzzle handle for QA tooling parity with single-puzzle
  // levels (Level 0). get_puzzle_state returns each module's live state
  // keyed by puzzle id; get_puzzle_internals exposes the live module
  // refs so fix-verify loops can call dial.spin / jigsaw.cycleSlot / etc.
  const compositePuzzle = {
    id: "redwoodComposite",
    getState: () => ({
      puzzles: Object.fromEntries(puzzles.map((p) => [p.id, p.getState()])),
    }),
    getInternals: () => ({
      seizedCrane,
      crowSquirrel,
      kittenCorral,
      hoodieJigsaw,
      finalBlockage,
    }),
  };
  const levelHandleMeta = scene.metadata?.level as
    | {
        setPuzzle?: (p: unknown) => void;
        registerPuzzles?: (puzzles: PuzzleModule[], levelState: Map<string, PuzzleState>) => void;
      }
    | undefined;
  levelHandleMeta?.setPuzzle?.(compositePuzzle);
  // Expose per-puzzle modules to the QA harness via getQAHints() — see
  // OvergrownConservatory for the motivating pattern. Also unlocks
  // forceSolvePuzzle + __startAtPuzzle deterministic fast-forwards.
  levelHandleMeta?.registerPuzzles?.(puzzles, levelState);

  // --- Environment enrichment: chain-draped engines + debris layer ---
  // Blender-authored `scrapyard_chains_env.glb` adds 9 chain-wrapped
  // motorcycle engine clusters, 5 catenary chain drapes between the
  // redwoods (with hanging rusted weights pulling them down), 28 ground-
  // debris props (cogs/bolts/copper nuts), 18 moss clumps on the back
  // wall, and 12 faint bioluminescent aura spots. All non-colliding
  // set-dressing — sits behind the playfield z-plane.
  try {
    const chainsEnv = await getAssetManager().instantiateModel(
      scene,
      "/assets/models/environment/scrapyard_chains_env.glb",
    );
    for (const mesh of chainsEnv.meshes) {
      mesh.isPickable = false;
      if (mesh.name !== "__root__" && !mesh.name.startsWith("__root__")) {
        // Receive-only: registering all the chain/debris meshes as
        // casters bloats the shadow frustum and the shadow map's
        // depth pass — flagged as a dominant L1 frame cost in the
        // 2026-07-11 fps A/B. Same pattern as the L2/L3 env loops.
        mesh.receiveShadows = true;
      }
    }
    // Freeze the chain-draped engines + hanging weights + debris. None
    // of these are animated, and they dominate the L1 mesh count.
    freezeStaticMeshes(chainsEnv.meshes, { excludePatterns: DEFAULT_STATIC_EXCLUDES });
    base.addDisposeCallback(() => chainsEnv.dispose());
  } catch (err) {
    console.warn("[RedwoodScrapyard] chains env failed to load:", err);
  }

  // --- Meshy-authored decorative scatter ---
  // Two hero props at multiple deep-z positions: rusted redwood trunks
  // (behind the playfield so the cats walk in front of them) and
  // ornate chain drapes (threaded between the trunks).
  const TRUNK_PLACEMENTS: ScatterPlacement[] = [
    { x: -4,  y: 0, z: -6, yaw: 0.1, scale: 1.1 },
    { x: 14,  y: 0, z: -6.5, yaw: -0.2, scale: 1.0 },
    { x: 32,  y: 0, z: -6, yaw: 0.3, scale: 1.15 },
    { x: 50,  y: 0, z: -6.4, yaw: -0.1, scale: 1.05 },
    { x: 70,  y: 0, z: -6, yaw: 0.2, scale: 1.2 },
    { x: 92,  y: 0, z: -6.3, yaw: -0.25, scale: 1.1 },
    { x: 115, y: 0, z: -6, yaw: 0.05, scale: 1.0 },
    { x: 138, y: 0, z: -6.2, yaw: 0.15, scale: 1.15 },
  ];
  const CHAIN_DRAPE_PLACEMENTS: ScatterPlacement[] = [
    // Phase C4 densified: 5 drapes → 10. Concept art p.12/15 has chains
    // threading through nearly every trunk; previous placement left
    // broad empty stretches where the canopy read as "one sad engine".
    { x: 6,   y: 12, z: -4.5, yaw: 0.15,  scale: 0.9  },
    { x: 18,  y: 14, z: -5,   yaw: -0.1,  scale: 1.05 },
    { x: 32,  y: 14, z: -5,   yaw: -0.1,  scale: 1.15 },
    { x: 45,  y: 13, z: -4.8, yaw: 0.22,  scale: 0.95 },
    { x: 58,  y: 13, z: -5,   yaw: 0.08,  scale: 0.95 },
    { x: 70,  y: 15, z: -5.2, yaw: -0.18, scale: 1.1  },
    { x: 82,  y: 14, z: -5,   yaw: -0.12, scale: 1.1  },
    { x: 95,  y: 12, z: -4.6, yaw: 0.1,   scale: 0.88 },
    { x: 108, y: 13, z: -5,   yaw: 0.06,  scale: 1.05 },
    { x: 125, y: 14, z: -5,   yaw: -0.2,  scale: 1.0  },
  ];
  // No shadowGenerator for the deep-z background scatter: these sit at
  // z≈-5..-7 behind the playfield, so their cast shadows land where no
  // player looks, while every registered mesh inflates the 2048 shadow
  // map's depth pass (part of the 2026-07-11 L1 fps regression fix).
  const trunkScatter = await scatterInstances(
    scene,
    // Meshy-generated steampunk trunk with chains + motorcycle parts
    // baked into the bark (2026-04-16, 141MB→6.2MB after optimize).
    // Replaces the earlier rusted_redwood_trunk which Gemini QA flagged
    // as "untextured brown cylinders."
    "/assets/models/environment/steampunk_redwood_trunk.glb",
    TRUNK_PLACEMENTS,
    { tag: "redwood" },
  );
  const chainScatter = await scatterInstances(
    scene,
    "/assets/models/environment/gothic_chain_drape.glb",
    CHAIN_DRAPE_PLACEMENTS,
    { tag: "chainDrape" },
  );
  base.addDisposeCallback(() => {
    trunkScatter.dispose();
    chainScatter.dispose();
  });

  // --- Hero scrap: Honda CB175 motorcycle crashed into the apex grove ---
  // Photogrammetry scan sourced via Sketchfab MCP. Sits half-buried in
  // front of the final blockage so the apex beat reads as "motorcycle
  // scrap strangling the redwoods" per the concept art. Loaded inline
  // (awaited) so HMR cannot lose the reference to a stale module.
  //
  // Phase C4 densified: the single hero bike read as a lonely set-piece.
  // Concept art p.12 shows motorcycles wedged between every pair of
  // trunks, so we now scatter CB175 instances at four deep-z positions
  // plus the hero position. Shared GLB is instanced, not re-loaded.
  try {
    const heroBike = await getAssetManager().instantiateModel(
      scene,
      "/assets/models/environment/hero/motorcycle_cb175.glb",
    );
    // The raw scan is ~22 units tall; scale 0.07 → ~1.5 m bike height.
    for (const root of heroBike.rootNodes) {
      root.position.set(138, 0.3, 0);
      root.scaling.setAll(0.07);
      root.rotation.y = Math.PI * 0.35;
      root.rotation.z = 0.18; // tipped over
    }
    for (const mesh of heroBike.meshes) {
      mesh.isPickable = false;
      shadowGen.addShadowCaster(mesh);
      mesh.receiveShadows = true;
    }
    base.addDisposeCallback(() => heroBike.dispose());

    // Background wrecks — half-buried at z≈-4.5 so cats walk past them.
    // Scale slightly smaller (0.055–0.065) so they read as "more scrap"
    // rather than competing with the hero bike for focus.
    const WRECK_POSITIONS: Array<{ x: number; y: number; z: number; yaw: number; roll: number; scale: number }> = [
      { x: 20,  y: 0.3, z: -4.5, yaw: 0.6,  roll: 0.45, scale: 0.06  },
      { x: 52,  y: 0.3, z: -4.2, yaw: -1.2, roll: 0.72, scale: 0.055 },
      { x: 78,  y: 0.3, z: -4.8, yaw: 0.9,  roll: -0.5, scale: 0.065 },
      { x: 104, y: 0.3, z: -4.3, yaw: -0.5, roll: 0.3,  scale: 0.06  },
    ];
    for (let i = 0; i < WRECK_POSITIONS.length; i++) {
      const p = WRECK_POSITIONS[i];
      const wreck = await getAssetManager().instantiateModel(
        scene,
        "/assets/models/environment/hero/motorcycle_cb175.glb",
      );
      for (const root of wreck.rootNodes) {
        root.position.set(p.x, p.y, p.z);
        root.scaling.setAll(p.scale);
        root.rotation.y = p.yaw;
        root.rotation.z = p.roll;
      }
      for (const mesh of wreck.meshes) {
        mesh.isPickable = false;
        shadowGen.addShadowCaster(mesh);
        mesh.receiveShadows = true;
      }
      freezeStaticMeshes(wreck.meshes, { excludePatterns: DEFAULT_STATIC_EXCLUDES });
      base.addDisposeCallback(() => wreck.dispose());
    }
  } catch (err) {
    console.warn("[RedwoodScrapyard] hero motorcycle failed to load:", err);
  }

  // --- Elder Kitty (hidden until the encounter cutscene reveals him) ---
  const elderKittyPos = new Vector3(145, 4, 0);
  const elderKitty = new ElderKitty(scene, elderKittyPos);
  elderKitty.mesh.setEnabled(false);
  shadowGen.addShadowCaster(elderKitty.mesh);
  base.addDisposeCallback(() => elderKitty.dispose());

  allInteractives.push({
    mesh: elderKitty.mesh,
    hidden: false,
    state: "revealed",
  });

  // ── Making Biscuits anchor (Kitty's bedroll) ──
  // Per SPEC-making-biscuits.md §2: "Elder Kitty's leather-and-brass
  // bedroll in a scrapyard alcove." Placed a few units west of the
  // Elder's reveal spot so Minna discovers it during the post-encounter
  // exploration beat. Hidden by default — revealed via Aura Sight.
  const kittyBiscuit = setupElderBiscuitAnchor({
    scene,
    elder: "kitty",
    minna,
    cutsceneSystem,
    position: new Vector3(elderKittyPos.x - 4, 0.3, 0.2),
    interactiveRegistry: allInteractives,
  });
  base.addDisposeCallback(() => kittyBiscuit.dispose());

  // ── Pre-encounter destination beacon ──
  //
  // Before Elder Kitty reveals himself the player has no visual goal at
  // the far east of the level — the QA pass flagged this as "no north-star"
  // for the long horizontal traverse. Place a low-intensity cool-dim
  // point light at the Elder's spawn so the player has something to
  // walk toward, without spoiling the reveal. Per canon (book p. 14)
  // Elder Kitty is a "slight black ninja" whose own glow is moonlit,
  // not a campfire — so the beacon matches his palette rather than the
  // rust-and-redwood of the scrapyard around it. That tonal offset is
  // what lets a distant watcher read as "that's the Elder, not more
  // scrap." The cutscene bumps this beacon's intensity during the
  // reveal to sell the starry-void moment.
  const elderBeacon = new PointLight(
    "elderKittyBeacon",
    new Vector3(elderKittyPos.x, elderKittyPos.y + 0.6, elderKittyPos.z),
    scene,
  );
  elderBeacon.diffuse = new Color3(0.45, 0.55, 0.72);
  elderBeacon.specular = new Color3(0.6, 0.7, 0.85);
  elderBeacon.intensity = 0.55;
  elderBeacon.range = 18;
  base.addDisposeCallback(() => elderBeacon.dispose());

  // ── Exit portal (scene-owned; opened by the Elder Kitty cutscene) ──
  const exitPortal = new Portal(scene, {
    key: "scrapyardPortal",
    position: new Vector3(148, 3, 0),
    label: "Portal to Overgrown Conservatory",
  });
  allInteractives.push({
    mesh: exitPortal.rootMesh,
    hidden: false,
    state: "revealed",
  });

  base.addDisposeCallback(() => exitPortal.dispose());

  // ── Register AuraSight interactives + connection lines ──
  auraSight.registerInteractives(allInteractives);

  // Build connection pairs by mesh name — robust against puzzle ordering.
  function findInfo(rootMeshName: string): InteractiveInfo | null {
    return allInteractives.find((i) => i.mesh.name === rootMeshName) ?? null;
  }
  const plateInfo = findInfo("interactive_cranePlate_root");
  const leverInfo = findInfo("interactive_craneLever_root");
  const nestInfo = findInfo("interactive_crowNest_root");
  const jigsawInfo = findInfo("interactive_hoodieJigsaw_root");
  const penInfo = findInfo("interactive_kittenPen_root");
  const blockageInfo = findInfo("interactive_finalBlockage_root");

  const connections: Array<[InteractiveInfo, InteractiveInfo]> = [];
  if (plateInfo && leverInfo) connections.push([plateInfo, leverInfo]);
  if (nestInfo && jigsawInfo) connections.push([nestInfo, jigsawInfo]);
  if (penInfo && blockageInfo) connections.push([penInfo, blockageInfo]);
  auraSight.registerConnections(connections);

  // ─── Level state (shell only) ───────────────────────────────────

  let elderEncounterDone = false;
  let levelComplete = false;

  // ─── Per-puzzle guidance (objective line + anchored cue chip) ─────
  //
  // Each puzzle's getCue() names its current step; the driver shows the
  // first unsolved puzzle's cue pinned to the thing to act on, with an
  // "(N/5)" objective line at the top. See LevelCueDriver.
  const cueDriver = new LevelCueDriver(
    [
      { puzzle: puzzles[0], objective: "Lift the seized crane" },
      { puzzle: puzzles[1], objective: "Feed the crows" },
      { puzzle: puzzles[2], objective: "Corral the kittens" },
      { puzzle: puzzles[3], objective: "Solve the hoodie jigsaw" },
      { puzzle: puzzles[4], objective: "Shatter the rusted blockage" },
    ],
    gameEvents,
  ).attach(scene);
  base.addDisposeCallback(() => cueDriver.dispose());

  // ─── Blockage shattered triggers the Elder Kitty cutscene ─────────
  base.addUnsub(gameEvents.on(GameEvents.BLOCKAGE_SHATTERED, (data) => {
    if (data?.key !== "finalBlockage") return;
    setTimeout(() => startElderEncounter(), 1500);
  }));

  // ─── Portal entry (guard: Elder cutscene already emits LEVEL_COMPLETE) ──
  base.addUnsub(gameEvents.on(GameEvents.PORTAL_ENTERED, () => {
    if (levelComplete) return;
    levelComplete = true;
    gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "RedwoodScrapyard" });
  }));

  function startElderEncounter(): void {
    if (elderEncounterDone) return;
    elderEncounterDone = true;

    // Broadcast the encounter moment for QA telemetry + AudioManager.
    // L2 and L3 emit the same event at the start of their Elder cutscenes;
    // L1 was the last scene where this was missing, which made "player met
    // the Elder" invisible to the recorder.
    gameEvents.emit(GameEvents.ELDER_ENCOUNTER, {
      elder: "kitty",
      level: "RedwoodScrapyard",
    });

    const elderPos = elderKittyPos;

    // Build Elder Kitty cutscene from dialogue data
    const dialogueSteps: CutsceneStep[] = ELDER_KITTY_ENCOUNTER.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    }));

    const steps: CutsceneStep[] = [
      // Canon reveal: per book p. 14, the scrap visibly "falls away
      // into a starry void" and Kitty appears at the far edge of that
      // void. We fade to black, reveal the Elder off-screen, then run
      // the `reveal` step which paints the scrap-into-stars curtain
      // via ElderRevealPostProcess (shader + particle emitters). The
      // final fade-in lands on the Elder as the void clears.
      { type: "fade", fadeDirection: "out", duration: 1200 },
      { type: "wait", duration: 400 },
      { type: "callback", fn: () => {
        // Reveal the Elder now that the screen is black.
        elderKitty.mesh.setEnabled(true);
        // Boost the beacon briefly so the fade-in lands on him as a
        // single cool glow in the void before the starry backdrop
        // resolves back to the scrapyard art.
        elderBeacon.intensity = 1.6;
      }},
      { type: "reveal", preset: "starry-void", duration: 2500 },
      { type: "fade", fadeDirection: "in", duration: 1200 },
      { type: "pan", target: { x: elderPos.x, y: elderPos.y }, duration: 2000 },
      { type: "wait", duration: 500 },
      ...dialogueSteps,
      { type: "shake", duration: 500, intensity: 0.008 },
      { type: "callback", fn: () => {
        gameEvents.emit(GameEvents.COOP_ABILITY_UNLOCKED, {
          abilityId: "resonantDrop",
          abilityName: "Resonant Drop",
        });
      }},
      { type: "wait", duration: 1000 },
      { type: "fade", fadeDirection: "out", duration: 1500 },
      { type: "callback", fn: () => {
        levelComplete = true;
        gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "RedwoodScrapyard" });
        exitPortal.open();
      }},
    ];

    cutsceneSystem.play(steps);
  }

  // ─── Dispose puzzles on scene teardown ──────────────────────────
  base.addDisposeCallback(() => {
    for (const puzzle of puzzles) {
      try { puzzle.dispose(); } catch (e) { console.warn("Puzzle dispose error:", e); }
    }
  });

  // ─── Wounds and Medicine: Grease Caches ─────────────────────────
  // Spec §4 "Secondary: Grease Caches" — Hollow-Knight-bench cadence,
  // placed between the harder puzzles so a wounded Basil has a safety
  // net without having to backtrack. Section spans:
  //   Section 2 (30-70): mid-level — between Crow-Squirrel and Kitten
  //   Section 3 (70-110): pre-apex
  //   Section 4 (110-135): pre-Final Blockage, tucked at the cliff base
  const greaseCaches = [
    new GreaseCache(scene, {
      key: "greaseCache_L1_s2",
      position: new Vector3(48, 1, 0),
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L1_s3",
      position: new Vector3(82, 1, 0),
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L1_s4",
      position: new Vector3(122, 1, 0),
      label: "Grease Cache",
    }),
  ];
  base.addDisposeCallback(() => {
    for (const cache of greaseCaches) {
      try { cache.dispose(); } catch (e) { console.warn("GreaseCache dispose error:", e); }
    }
  });

  // ─── Game Loop ──────────────────────────────────────────────────

  base.registerGameLoop(
    (dt, sceneTime) => {
      // Tick each puzzle and refresh its entry in levelState so
      // finalBlockage's gating always sees fresh solved flags.
      for (const puzzle of puzzles) {
        puzzle.update(dt, sceneTime);
        levelState.set(puzzle.id, puzzle.getState());
      }

      // Wounds/Medicine: tick every grease cache so the respawn timer
      // advances and the brass-lid pulse breathes on ready caches.
      for (const cache of greaseCaches) {
        cache.tick(dt);
      }

      // Portal entry is tracked once the blockage has been shattered
      // (Elder cutscene opens the portal at its end).
      if (finalBlockage.getState().solved) {
        exitPortal.checkEntry(minna.mesh.position, basil.mesh.position);
      }

      // Hoodie Papoose — keeps carried kittens locked to `calmed` so a
      // mid-carry purr timeout can't flip them back to frantic. No-op
      // until the jigsaw activates the carrier.
      papooseCarrier.update();

      // Co-op ability update
      resonantDrop.update(dt);

      // Elder Kitty idle bob + glow orbit (only matters once revealed).
      if (elderKitty.mesh.isEnabled()) {
        elderKitty.update(sceneTime, dt);
      }

      // Making Biscuits: tick the minigame + proximity prompt for the
      // Kitty bedroll anchor.
      kittyBiscuit.update(dt);
    },
    {
      isLevelComplete: () => levelComplete,
      onAbilityKey: (key, pressed, released) => {
        // Biscuits (B / A / D) take priority when a session is active
        // so the paw taps don't race with movement or puzzle binds.
        if (kittyBiscuit.onAbilityKey(key, pressed, released)) {
          return true;
        }

        // Give each puzzle first crack at the key (e.g. crane's 1/2 binds)
        for (const puzzle of puzzles) {
          if (puzzle.onAbilityKey && puzzle.onAbilityKey(key, pressed, released)) {
            return true;
          }
        }

        // Q = Aura Sight toggle (override to also hide the jigsaw pattern)
        if (key === "q" && pressed) {
          if (auraSight.isActive) {
            minnaAbilities.deactivateAbility("auraSight");
            hoodieJigsaw.hidePattern();
          } else {
            minnaAbilities.activateAbility("auraSight");
          }
          return true;
        }

        // E = Minna context-sensitive: drop carried food, OR pick up nearby
        // food, OR toggle Shadow Slip. The drop-first ordering matters —
        // without it, pressing E while carrying food silently activated
        // Shadow Slip mid-delivery (footgun flagged in the L1 QA pass).
        if (key === "e" && pressed) {
          const carried = crowSquirrel.carriedBy(minna.mesh);
          if (carried) {
            carried.drop();
            return true;
          }
          const foodPickedUp = crowSquirrel.tryPickupFood(minna.mesh);
          if (!foodPickedUp) {
            if (shadowSlip.isActive) {
              minnaAbilities.deactivateAbility("shadowSlip");
            } else {
              minnaAbilities.activateAbility("shadowSlip");
            }
          }
          return true;
        }

        // , = Basil interact. Precedence:
        //   1) Grease Cache pickup (Wounds/Medicine — low-priority so
        //      it doesn't swallow puzzle interactions; only activates
        //      when Basil is actually in radius AND the harness needs
        //      healing)
        //   2) Jigsaw cycle nearest slot
        //   3) Food pickup
        if (key === "," && pressed) {
          for (const cache of greaseCaches) {
            if (cache.isReady && cache.isInInteractRange(basil.mesh.position)) {
              if (cache.consume(base.harnessWounds, basil.mesh.position)) {
                return true;
              }
            }
          }
          if (!hoodieJigsaw.tryCycleNearestSlot()) {
            crowSquirrel.tryPickupFood(basil.mesh);
          }
          return true;
        }

        // Co-op: F + M = Resonant Drop
        if (key === "f+m" && pressed) {
          tryCoopAbility(resonantDrop, "resonantDrop");
          return true;
        }

        return false;
      },
    },
  );

  // --- Start ambient audio ---
  setTimeout(() => audioManager.startAmbient(), 500);

  // --- Emit level started ---
  gameEvents.emit(GameEvents.LEVEL_STARTED, "RedwoodScrapyard");

  // --- Level intro cutscene with control hints ---
  const _introLines = LEVEL_INTRO["RedwoodScrapyard"] ?? [];
  const _introSteps: CutsceneStep[] = [
    { type: "fade", fadeDirection: "in", duration: 1200, color: { r: 0, g: 0, b: 0 } },
    { type: "wait", duration: 300 },
    // Push east to show the seized crane silhouette before handing control
    // back to the player — establishes direction of travel as "go right".
    { type: "pan", target: { x: 20, y: 4 }, duration: 1800, ease: "cubic" },
    { type: "wait", duration: 400 },
    ..._introLines.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    })),
    // Return framing to the cats before releasing control.
    { type: "pan", target: { x: 8, y: 3 }, duration: 900, ease: "cubic" },
    { type: "dialogue", speaker: "", text: controlHintForMode(inputManager.soloMode), duration: 3500 },
    { type: "wait", duration: 300 },
  ];
  // `playDeferred` (not raw setTimeout) so that `__startAtPuzzle` can cancel
  // this pending intro before the timer fires — see
  // docs/qa/analysis/startatpuzzle-audit-2026-04-18.md.
  cutsceneSystem.playDeferred(_introSteps, 600);

  return {
    physicsSystem,
    cameraSystem,
    inputManager,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    audioManager,
    cutsceneSystem,
  };
}

// ─── Environment Helpers ──────────────────────────────────────────────

/**
 * Creates a procedural background with gradient sky and
 * redwood trunk silhouettes for depth.
 */
function createBackground(scene: Scene): void {
  const W = SCRAPYARD.WORLD_WIDTH;
  const H = SCRAPYARD.WORLD_HEIGHT;

  // Sky gradient plane (far back)
  const skyPlane = MeshBuilder.CreatePlane("skyPlane", { width: W * 1.5, height: H * 1.5 }, scene);
  skyPlane.position = new Vector3(W / 2, H / 2, -15);
  skyPlane.isPickable = false;

  const skyMat = new StandardMaterial("skyMat", scene);
  skyMat.diffuseColor = Color3.FromHexString(SCRAPYARD.COLORS.SKY_TOP);
  skyMat.emissiveColor = Color3.FromHexString(SCRAPYARD.COLORS.SKY_BOTTOM).scale(0.3);
  skyMat.specularColor = Color3.Black();
  skyMat.backFaceCulling = false;
  skyPlane.material = skyMat;
  tagAs(skyPlane, "placeholder");

  // Redwood trunk silhouettes at various depths
  const trunkPositions = [10, 30, 50, 75, 95, 120, 140];
  for (let i = 0; i < trunkPositions.length; i++) {
    const tx = trunkPositions[i];
    const trunkWidth = 2 + Math.random() * 1.5;
    const trunkHeight = H * 0.7 + Math.random() * 10;

    const trunk = MeshBuilder.CreateBox(
      `bgTrunk_${i}`,
      { width: trunkWidth, height: trunkHeight, depth: 0.1 },
      scene,
    );
    trunk.position = new Vector3(tx, trunkHeight / 2, -8 - Math.random() * 4);
    trunk.isPickable = false;

    const trunkMat = new StandardMaterial(`bgTrunkMat_${i}`, scene);
    trunkMat.diffuseColor = Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_DARK);
    trunkMat.emissiveColor = Color3.FromHexString(SCRAPYARD.COLORS.REDWOOD_DARK).scale(0.1);
    trunkMat.alpha = 0.35 + Math.random() * 0.15;
    trunkMat.specularColor = Color3.Black();
    trunk.material = trunkMat;
    tagAs(trunk, "placeholder");

    // Branch foliage ellipsoids
    for (let b = 0; b < 3; b++) {
      const branch = MeshBuilder.CreateSphere(
        `bgBranch_${i}_${b}`,
        { diameter: 4 + Math.random() * 3, segments: 8 },
        scene,
      );
      branch.scaling = new Vector3(1.5, 0.4, 0.5);
      branch.position = new Vector3(
        tx + (Math.random() - 0.5) * 5,
        trunkHeight * 0.3 + b * 8 + Math.random() * 4,
        trunk.position.z - 0.5,
      );
      branch.isPickable = false;

      const branchMat = new StandardMaterial(`bgBranchMat_${i}_${b}`, scene);
      branchMat.diffuseColor = Color3.FromHexString(SCRAPYARD.COLORS.MOSS);
      branchMat.alpha = 0.12;
      branchMat.specularColor = Color3.Black();
      branch.material = branchMat;
      tagAs(branch, "placeholder");
    }
  }
}

/**
 * Creates semi-transparent fog layer planes at different Z depths
 * to simulate parallax atmospheric haze.
 */
function createFogLayers(scene: Scene): void {
  const W = SCRAPYARD.WORLD_WIDTH;
  const H = SCRAPYARD.WORLD_HEIGHT;

  // Z=-3 layer removed — it sat between camera (z=9) and cats (z=0)
  // and compounded with the haze parallax at z=-12 + post-process
  // chromatic aberration to produce an "out of focus" read on L1.
  // Keeping the far + mid layers; they read as atmospheric depth
  // without touching the play plane.
  const fogLayers = [
    { z: -14, alpha: 0.08, color: SCRAPYARD.COLORS.REDWOOD_DARK },
    { z: -8,  alpha: 0.05, color: SCRAPYARD.COLORS.SKY_BOTTOM },
  ];

  for (let i = 0; i < fogLayers.length; i++) {
    const layer = fogLayers[i];
    const fogPlane = MeshBuilder.CreatePlane(
      `fogLayer_${i}`,
      { width: W * 1.3, height: H },
      scene,
    );
    fogPlane.position = new Vector3(W / 2, H / 2, layer.z);
    fogPlane.isPickable = false;

    const fogMat = new StandardMaterial(`fogLayerMat_${i}`, scene);
    fogMat.diffuseColor = Color3.FromHexString(layer.color);
    fogMat.emissiveColor = Color3.FromHexString(layer.color).scale(0.15);
    fogMat.alpha = layer.alpha;
    fogMat.specularColor = Color3.Black();
    // These planes ARE the fake fog — letting scene fog shade them too
    // double-counts haze and feeds the L1 white-glare stack (see
    // ParallaxBackground.addLayer's fogEnabled note).
    fogMat.fogEnabled = false;
    fogMat.backFaceCulling = false;
    fogPlane.material = fogMat;
    tagAs(fogPlane, "placeholder");
  }
}

/**
 * Drifting moss spores / firefly motes spanning the level width. Adds
 * life to the mid-foreground fog without pulling focus from the cats.
 * Uses a single GPU-friendly ParticleSystem rather than per-section
 * emitters to keep draw calls flat.
 */
function createAtmosphereMotes(scene: Scene): ParticleSystem {
  const W = SCRAPYARD.WORLD_WIDTH;
  const H = SCRAPYARD.WORLD_HEIGHT;

  // Phase C4 densified: 400 → 900 max particles, 50 → 120 emit rate.
  // Larger size variance + broader z-spread so the motes fill the whole
  // depth-band rather than sitting in a single midground plane.
  const ps = new ParticleSystem("redwoodMotes", 200, scene);
  ps.emitter = new Vector3(W / 2, H / 3, 0);
  ps.minEmitBox = new Vector3(-W / 2, -4, -4);
  ps.maxEmitBox = new Vector3(W / 2, 6, 3);
  ps.minLifeTime = 8;
  ps.maxLifeTime = 16;
  ps.minSize = 0.01;
  ps.maxSize = 0.05;
  ps.emitRate = 30;
  ps.minEmitPower = 0.05;
  ps.maxEmitPower = 0.35;
  ps.updateSpeed = 0.02;
  ps.gravity = new Vector3(0, 0.08, 0); // drifts up very slowly
  ps.direction1 = new Vector3(-0.1, 0.2, 0);
  ps.direction2 = new Vector3(0.1, 0.5, 0);
  ps.color1 = new Color4(0.0, 1.0, 0.53, 0.3);
  ps.color2 = new Color4(0.8, 0.9, 0.4, 0.15);
  ps.colorDead = new Color4(0.2, 0.4, 0.1, 0);
  ps.blendMode = ParticleSystem.BLENDMODE_ADD;
  ps.start();
  return ps;
}

/**
 * Creates invisible shadow zone trigger volumes for Shadow Slip.
 */
function createShadowZones(scene: Scene): ShadowZoneInfo[] {
  const zones: ShadowZoneInfo[] = [];

  const zoneDefs = [
    { position: new Vector3(12, 1.5, 0), width: 5, height: 1.5, depth: 1.5 },
    { position: new Vector3(38, 2.0, 0), width: 6, height: 1.2, depth: 1.5 },
    { position: new Vector3(65, 1.8, 0), width: 5, height: 1.5, depth: 1.5 },
    { position: new Vector3(88, 2.5, 0), width: 7, height: 1.3, depth: 1.5 },
    { position: new Vector3(115, 2.0, 0), width: 5, height: 1.5, depth: 1.5 },
    { position: new Vector3(138, 1.5, 0), width: 6, height: 1.2, depth: 1.5 },
  ];

  for (let i = 0; i < zoneDefs.length; i++) {
    const def = zoneDefs[i];
    const mesh = MeshBuilder.CreateBox(
      `shadowZone_${i}`,
      { width: def.width, height: def.height, depth: def.depth },
      scene,
    );
    mesh.position = def.position;
    mesh.isVisible = false;
    mesh.isPickable = false;

    // Semi-transparent dark visual
    const debugVisual = MeshBuilder.CreateBox(
      `shadowZoneVisual_${i}`,
      { width: def.width, height: def.height, depth: def.depth },
      scene,
    );
    debugVisual.position = def.position;
    debugVisual.isVisible = WORLD_DEBUG.SHOW_SHADOW_ZONE_VISUALS;
    debugVisual.isPickable = false;
    const darkMat = new StandardMaterial(`shadowZoneMat_${i}`, scene);
    darkMat.diffuseColor = new Color3(0.02, 0.01, 0.04);
    darkMat.emissiveColor = new Color3(0.01, 0.005, 0.02);
    darkMat.alpha = 0.35;
    debugVisual.material = darkMat;

    zones.push({ mesh });
  }

  return zones;
}

/**
 * Creates the ground plane and platforming surfaces.
 */
function createGroundAndPlatforms(
  scene: Scene,
  physicsSystem: PhysicsSystem,
  shadowGen: ShadowGenerator,
): { ground: Mesh | null; platforms: Mesh[] } {
  const W = SCRAPYARD.WORLD_WIDTH;

  // Ground
  const ground = physicsSystem.createGround(W, SCRAPYARD.GROUND_DEPTH, W / 2);
  const groundMat = new PBRMaterial("groundMat", scene);
  groundMat.albedoColor = new Color3(0.12, 0.09, 0.07);
  groundMat.metallic = 0.05;
  groundMat.roughness = 0.95;
  groundMat.emissiveColor = new Color3(0.01, 0.005, 0.01);
  // C6: noisy loam-floor bump so the ground reads as soil, not a flat
  // chocolate-brown plane. Tiled 30× because the scrapyard is 154 units
  // wide — one large stamp would smear.
  const groundNoise = getOrMakeNoiseBump(scene, "scrapyard_loam", 0.7);
  groundNoise.uScale = 30;
  groundNoise.vScale = 3;
  groundMat.bumpTexture = groundNoise;
  groundMat.bumpTexture.level = 0.5;
  if (ground) {
    ground.material = groundMat;
    ground.receiveShadows = true;
  }

  // Platform material: weathered wood with a warm amber emissive rim so it
  // reads as walkable through fog and against the brown forest dressing.
  // Rusted brass + weathered wood look for the platform staircase.
  // Gemini QA flagged the original flat brown as "untextured prototype."
  const platformMat = new PBRMaterial("platformMat", scene);
  platformMat.albedoColor = new Color3(0.55, 0.38, 0.18);  // warm wood
  platformMat.metallic = 0.35;                               // rusted metal sheen
  platformMat.roughness = 0.65;
  platformMat.emissiveColor = new Color3(0.08, 0.04, 0.02); // subtle warmth
  // C6: wood-grain bump on the planks. Tiled 4× per platform width to
  // give short plank-sized highlights rather than a single long fiber.
  const plankNoise = getOrMakeNoiseBump(scene, "scrapyard_plank", 0.45);
  plankNoise.uScale = 4;
  plankNoise.vScale = 1;
  platformMat.bumpTexture = plankNoise;
  platformMat.bumpTexture.level = 0.3;


  const platforms: Mesh[] = [];
  // Platform geometry lives in src-babylon/levels/layouts/ (reachability lint).
  for (const def of REDWOOD_SCRAPYARD_PLATFORMS.platforms) {
    const plat = physicsSystem.createPlatform(
      def.name,
      def.w,
      platformHeight(def),
      platformDepth(def),
      new Vector3(def.x, def.y, 0),
    );
    plat.material = platformMat;
    plat.receiveShadows = true;
    shadowGen.addShadowCaster(plat);
    platforms.push(plat);
  }

  return { ground, platforms };
}
